/**
 * Persisted state for the per-section guide box.
 *
 * - ``collapsed`` (per section): tuck away a section's guide once learned.
 * - ``dismissed`` (per audience key): a real user can turn the guidance off
 *   everywhere ("Non mostrare più"); demo visitors keep it on. Keyed by the
 *   same ``onboardingKey()`` as the tour, so it's per browser for demo and per
 *   user for real accounts.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { zustandStorage } from "@/lib/storage";

interface SectionGuideState {
  collapsed: Record<string, boolean>;
  dismissed: Record<string, boolean>;
  toggle: (section: string) => void;
  dismiss: (audienceKey: string) => void;
  /** Re-enable the guides everywhere (Settings → "riattiva aiuti"). */
  restore: (audienceKey: string) => void;
}

export const useSectionGuideStore = create<SectionGuideState>()(
  persist(
    (set) => ({
      collapsed: {},
      dismissed: {},
      toggle: (section) =>
        set((s) => ({
          collapsed: { ...s.collapsed, [section]: !s.collapsed[section] },
        })),
      dismiss: (audienceKey) =>
        set((s) => ({
          dismissed: { ...s.dismissed, [audienceKey]: true },
        })),
      restore: (audienceKey) =>
        set((s) => ({
          dismissed: { ...s.dismissed, [audienceKey]: false },
          collapsed: {},
        })),
    }),
    {
      name: "ando.section-guide",
      storage: createJSONStorage(() => zustandStorage),
    },
  ),
);
